import { FC, useState } from "react";
import GenericModalV2 from "../../../shared/GenericModal/GenericModal";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import Typhography from "../../../shared/Typhography/Typhography";
import {
  CalendarDotIcon,
  CalendarDotsIcon,
  CalendarIcon,
} from "@phosphor-icons/react";
import cx from "classnames";
import style from "./RecurrenceScopeDialog.module.scss";

type Scope = "this" | "this_and_following" | "all";

interface RecurrenceScopeDialogProps {
  isOpen: boolean;
  action: "edit" | "delete";
  onClose: () => void;
  onConfirm: (scope: string) => void;
  /** Optional mapping from internal Scope to API value. Defaults to identity. */
  scopeMapping?: Partial<Record<Scope, string>>;
}

const SCOPE_OPTIONS: {
  label: string;
  description: string;
  value: Scope;
  Icon: typeof CalendarDotIcon;
}[] = [
  {
    label: "Solo questo evento",
    description: "Modifica solo l'occorrenza selezionata",
    value: "this",
    Icon: CalendarDotIcon,
  },
  {
    label: "Questo e i successivi",
    description: "Modifica questa occorrenza e tutte quelle future",
    value: "this_and_following",
    Icon: CalendarDotsIcon,
  },
  {
    label: "Tutta la serie",
    description: "Modifica tutti gli eventi di questa ricorrenza",
    value: "all",
    Icon: CalendarIcon,
  },
];

const RecurrenceScopeDialog: FC<RecurrenceScopeDialogProps> = ({
  isOpen,
  action,
  onClose,
  onConfirm,
  scopeMapping,
}) => {
  const [selected, setSelected] = useState<Scope>("this");

  const title =
    action === "edit" ? "Modifica ricorrenza" : "Elimina ricorrenza";
  const subtitle =
    action === "edit"
      ? "Scegli quali eventi vuoi modificare."
      : "Scegli quali eventi vuoi eliminare.";

  const handleConfirm = () => {
    const mapped = scopeMapping?.[selected] ?? selected;
    onConfirm(mapped);
  };

  return (
    <GenericModalV2
      isOpen={isOpen}
      title={title}
      size="sm"
      onClose={onClose}
      Footer={
        <>
          <CustomButton color="outline" onClick={onClose}>
            Annulla
          </CustomButton>
          <CustomButton color="secondary" onClick={handleConfirm}>
            Conferma
          </CustomButton>
        </>
      }
    >
      <div className="d-flex flex-column gap-2 mt-3">
        <Typhography size="regular-14" className={style.subtitle}>
          {subtitle}
        </Typhography>

        {SCOPE_OPTIONS.map((opt) => (
          <div
            key={opt.value}
            className={cx(style.option, { [style.selected]: selected === opt.value })}
            onClick={() => setSelected(opt.value)}
          >
            <div className={style.dot} />
            <opt.Icon className={style.icon} weight="regular" />
            <div className="d-flex flex-column gap-1">
              <Typhography size="bold-14" className={style.label}>
                {opt.label}
              </Typhography>
              <Typhography size="regular-12" className={style.label}>
                {opt.description}
              </Typhography>
            </div>
          </div>
        ))}
      </div>
    </GenericModalV2>
  );
};

export default RecurrenceScopeDialog;
